import { supabase, supabaseEndpoint } from '../client';

export interface SendEmailInput {
  templateKey: string;
  to: string | string[];
  variables?: Record<string, unknown>;
  orgId?: string | null;
  cc?: string[];
  bcc?: string[];
  /** Render + queue + mark as sent without actually contacting Resend. */
  dryRun?: boolean;
}

export interface SendEmailResult {
  ok: boolean;
  message_id?: string;
  provider_message_id?: string;
  error?: string;
}

/**
 * Invokes the `send-email` Edge Function via a plain `fetch`.
 *
 * NOTE -- this used to go through `supabase.functions.invoke()`, but we
 * intentionally bypass that abstraction. Two reasons, both bitten in the
 * 2026-05-17 incident:
 *
 *   1. CORS surface area. The supabase-js SDK adds a moving target of
 *      headers to every outbound request (`x-client-info`, `x-retry-count`,
 *      etc.). Each new release silently expands the set; every Edge
 *      Function's `Access-Control-Allow-Headers` would then need a
 *      lockstep update. Worse, this app's `errorReporter.ts` monkey-
 *      patches `globalThis.fetch` and adds `x-request-id` to ALL outbound
 *      fetches for cross-request tracing -- that header must also be in
 *      the allow-list (it now is, but missing it is a silent
 *      `TypeError: Failed to fetch` after a successful preflight).
 *
 *      By calling `fetch` ourselves with an explicit, minimal header set
 *      we own the contract and cannot drift accidentally. (`patchedFetch`
 *      will still attach `x-request-id`, but that's intentional and the
 *      server allows it.)
 *
 *   2. Error visibility. `FunctionsFetchError` and `FunctionsHttpError`
 *      hide the actual fetch error and the response body in different
 *      shapes across SDK versions. A direct fetch hands us the raw
 *      Response (or the underlying TypeError on network failure), so the
 *      toast shown to the inviter is always the real reason, not a stack
 *      of opaque wrapper messages.
 *
 * The function does:
 *   1. Calls `core.rpc_emit_email` under the caller's JWT (so authorization
 *      is enforced in the database -- platform admin OR org access required).
 *   2. POSTs the rendered email to Resend using the RESEND_API secret.
 *   3. Updates the queued `core.email_messages` row via the same SECURITY
 *      DEFINER RPC chain (`rpc_email_dispatch_load`, `rpc_email_mark_sent`,
 *      `rpc_email_mark_failed`).
 *
 * Always returns `{ ok, error? }` rather than throwing -- transactional callers
 * (sendProposal etc.) check `ok` and surface the failure without rolling back
 * the state change that triggered the email.
 */
export async function sendEmail(input: SendEmailInput): Promise<SendEmailResult> {
  const sb = supabase();

  const { data: sess } = await sb.auth.getSession();
  const accessToken = sess.session?.access_token;
  if (!accessToken) {
    return {
      ok: false,
      error: 'Your session has expired. Please sign in again and retry.',
    };
  }

  const { url: baseUrl, anonKey } = supabaseEndpoint();
  const url = `${baseUrl.replace(/\/$/, '')}/functions/v1/send-email`;

  let resp: Response;
  try {
    resp = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: anonKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        template_key: input.templateKey,
        to: input.to,
        variables: input.variables ?? {},
        org_id: input.orgId ?? undefined,
        cc: input.cc,
        bcc: input.bcc,
        dry_run: input.dryRun ?? false,
      }),
    });
  } catch (err) {
    const name = err instanceof Error ? err.name : 'Error';
    const msg = err instanceof Error ? err.message : String(err);
    const cause = err instanceof Error && err.cause !== undefined ? String(err.cause) : null;
    return {
      ok: false,
      error: cause
        ? `Network failure invoking send-email (${name}): ${msg} | cause: ${cause}`
        : `Network failure invoking send-email (${name}): ${msg}`,
    };
  }

  let body: SendEmailResult | null = null;
  let raw: string | null = null;
  try {
    raw = await resp.text();
    if (raw) body = JSON.parse(raw) as SendEmailResult;
  } catch {
    // body wasn't JSON; raw still holds the text payload (if any)
  }

  if (!resp.ok) {
    const detail = body?.error ?? raw ?? `HTTP ${resp.status}`;
    return { ok: false, error: detail };
  }

  return body ?? { ok: false, error: 'send-email returned an empty body.' };
}

/**
 * Send a test render of a template to an address (defaults to the signed-in
 * admin). Uses the template's `sample_data` for variables.
 */
export async function sendTestEmail(
  templateKey: string,
  to: string,
  sampleVariables: Record<string, unknown>,
): Promise<SendEmailResult> {
  return sendEmail({
    templateKey,
    to,
    variables: sampleVariables,
  });
}
